import {
  Button,
  EyeIcon,
  Flex,
  HideEyeIcon,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'react-router';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { LinkWithBasename } from '@jane/bloom/components/linkWithBasename';
import { useLogin } from '@jane/bloom/hooks/account/useLogin';
import { AuthErrorMessage } from '@jane/bloom/types/schemas/marketplace/authErrors';

import styles from './welcome.module.css';

const migratedUserErrorMessage =
  'We recently made some changes to the site. If you are unable to login, you may need to reset your password.';

interface WelcomeProps {
  isBloomMessagingForMigratedUsersEnabled?: boolean;
  userEmail?: string;
}

export function Welcome({
  isBloomMessagingForMigratedUsersEnabled,
  userEmail,
}: WelcomeProps) {
  const { fetcher, login } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [visibleMigratedMessage, setVisibleMigratedMessage] = useState(false);

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      password: '',
    },
    mode: 'all',
  });

  const submitSignInForm = ({ password }: { password: string }) => {
    if (userEmail) {
      login({ email: userEmail, password });
    }
  };

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && !fetcher.data.success) {
      setShowPasswordError(true);
      switch (fetcher.data.errors['message'][0]) {
        case AuthErrorMessage.INVALID_USER_OR_PASSWORD:
        case AuthErrorMessage.USER_ALMOST_LOCKED:
          setError('password', {
            message: fetcher.data.errors['message'][0],
          });
          setVisibleMigratedMessage(true);
          break;
        case AuthErrorMessage.USER_LOCKED:
          setError('root', {
            message: fetcher.data.errors['message'][0],
          });
          setVisibleMigratedMessage(true);
          break;
        default:
          break;
      }
    }

    document.getElementById('password')?.addEventListener('keydown', () => {
      setShowPasswordError(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.state]);

  return (
    <main className={styles['main']}>
      <Breadcrumbs
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Sign in' }]}
        className={styles['loginBreadcrumbs']}
      />
      <Flex
        className={styles['content']}
        alignItems="center"
        flexDirection="column"
      >
        <Typography variant="header" className={styles['headerText']}>
          Welcome back!
        </Typography>
        <Flex flexDirection="column" maxWidth={435} width="100%">
          <Flex flexDirection="column" className={styles['loginContainer']}>
            <Form
              name="Account Sign In Form"
              onSubmit={handleSubmit(submitSignInForm)}
            >
              <div className={styles['emailContainer']}>
                <Typography variant="body-bold" color="grays-black">
                  {userEmail}
                </Typography>
                <LinkWithBasename to="/login" variant="reefer-underline">
                  Edit
                </LinkWithBasename>
              </div>
              <Flex flexDirection="column">
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: 'Password is required' }}
                  render={({ field }) => (
                    <TextField
                      disableMobileInputStyling
                      type={showPassword ? 'text' : 'password'}
                      autocomplete="off"
                      label="Password"
                      name="password"
                      className={clsx({
                        [styles['errorPassTextField']]:
                          showPasswordError && errors.password,
                      })}
                      // Override default Reefer error message for passwords
                      onChange={(password) => {
                        field.onChange(password);
                      }}
                      onBlur={() => {
                        setShowPasswordError(true);
                      }}
                      endIcon={
                        showPassword ? (
                          <EyeIcon
                            altText="Hide current password"
                            onClick={() => setShowPassword(false)}
                          />
                        ) : (
                          <HideEyeIcon
                            altText="Show current password"
                            onClick={() => setShowPassword(true)}
                          />
                        )
                      }
                    />
                  )}
                />
                {errors.password && showPasswordError && (
                  <Typography variant="mini" color="error" mt={8}>
                    {errors.password.message}
                  </Typography>
                )}
                <LinkWithBasename
                  to="/forgot-password"
                  variant="reefer-underline"
                  className={styles['forgotPassContainer']}
                >
                  Forgot password?
                </LinkWithBasename>
                <Button
                  label="Sign in"
                  full
                  type="submit"
                  disabled={!isValid}
                  mt={16}
                  loading={fetcher.state !== 'idle'}
                  variant="primary"
                />
                {isBloomMessagingForMigratedUsersEnabled &&
                  visibleMigratedMessage && (
                    <Typography
                      variant="body"
                      color="grays-black"
                      mt={8}
                      className={styles['lockedAccountMessage']}
                    >
                      {migratedUserErrorMessage}
                    </Typography>
                  )}
                {errors.root && showPasswordError && (
                  <Typography
                    variant="body"
                    color="error"
                    mt={8}
                    className={styles['lockedAccountMessage']}
                  >
                    {errors.root.message}
                  </Typography>
                )}
              </Flex>
            </Form>
          </Flex>
        </Flex>
      </Flex>
    </main>
  );
}
